import type { MultiPolygon, Polygon } from "geojson";
import polygonClipping from "polygon-clipping";
import { pcPolygonToGeoJSON, toPolygonClippingGeom } from "./union";

type AnyPolygon = Polygon | MultiPolygon;

/** Wrap a polygon-clipping result, collapsing an empty one to null so callers must handle
 *  "nothing left" rather than passing an empty MultiPolygon around. */
function fromPolygonClipping(result: polygonClipping.MultiPolygon): AnyPolygon | null {
	if (result.length === 0) return null;
	if (result.length === 1) return { type: "Polygon", coordinates: pcPolygonToGeoJSON(result[0]) };
	return { type: "MultiPolygon", coordinates: result.map(pcPolygonToGeoJSON) };
}

/** The part of `subject` that lies inside `clip`, or null when they do not overlap. */
export function intersectPolygons(subject: AnyPolygon, clip: AnyPolygon): AnyPolygon | null {
	return fromPolygonClipping(polygonClipping.intersection(toPolygonClippingGeom(subject), toPolygonClippingGeom(clip)));
}

/** `subject` with every one of `cutters` removed, or null when nothing survives. */
export function subtractPolygons(subject: AnyPolygon, cutters: AnyPolygon[]): AnyPolygon | null {
	if (cutters.length === 0) return subject;
	return fromPolygonClipping(
		polygonClipping.difference(toPolygonClippingGeom(subject), ...cutters.map(toPolygonClippingGeom)),
	);
}

/** One polygon covering all of `shapes`, or null when there are none. */
export function unionPolygonGeometries(shapes: AnyPolygon[]): AnyPolygon | null {
	if (shapes.length === 0) return null;
	const [first, ...rest] = shapes.map(toPolygonClippingGeom);
	return fromPolygonClipping(polygonClipping.union(first, ...rest));
}

/** Every disconnected piece of a polygon, as its own Polygon. */
export function polygonParts(geometry: AnyPolygon): Polygon[] {
	if (geometry.type === "Polygon") return [geometry];
	return geometry.coordinates.map((coordinates) => ({ type: "Polygon", coordinates }));
}
